/**
 * Wizard step 4: review and create.
 *
 * Nothing here edits the draft — the summary is read-only and the only action is
 * the create call. After a successful create the draft is cleared so the next
 * visit starts empty, and the browser lands on the created event's detail page.
 */

import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { LIMITS } from '@shared/constants'
import type { CreateEventInput } from '@shared/types'
import { isValidLocalDateTime, trimmedLength } from '@shared/validation'

import { ErrorAlert } from '../../../components/layout/states'
import { WizardFooter } from '../../../components/layout/wizard'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Stat, StatGrid } from '../../../components/ui/primitives'
import { useCreateEvent } from '../../../hooks/use-events'
import { currentLocale } from '../../../lib/i18n'
import { mutationErrorText } from '../../../lib/mutation-error'
import { activityLabelKey, cityLabelKey, formatLocalDateTime } from '../../../lib/format'
import { useEventDraft } from '../../../stores/drafts'

export function NewEventReviewPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { draft, reset } = useEventDraft()
  const createEvent = useCreateEvent()

  const locale = currentLocale()

  const title = draft.title.trim()
  const titleValid =
    trimmedLength(title) >= LIMITS.eventTitleMinLength &&
    trimmedLength(title) <= LIMITS.eventTitleMaxLength
  const startValid = isValidLocalDateTime(draft.eventDateStart)
  // The end must describe a window: without this the confirmation step would post a
  // value the Worker refuses, and the user would lose the whole draft review.
  const endValid = draft.eventDateEnd === null || draft.eventDateEnd > draft.eventDateStart
  // The review step names what blocks it: a generic refusal leaves the organizer to
  // guess which of the earlier steps to go back to.
  const blocker = !titleValid
    ? t('eventNew.titleError')
    : draft.activities.length === 0
      ? t('eventNew.activitiesError')
      : !startValid
        ? t('eventNew.dateError')
        : !endValid
          ? t('eventNew.dateOrderError')
          : null
  const canSubmit = blocker === null

  const timeValue = draft.eventDateEnd
    ? `${formatLocalDateTime(draft.eventDateStart, locale)} → ${formatLocalDateTime(
        draft.eventDateEnd,
        locale,
      )}`
    : formatLocalDateTime(draft.eventDateStart, locale)

  function submit() {
    if (!canSubmit) {
      return
    }
    const input: CreateEventInput = {
      title,
      city: draft.city,
      eventDateStart: draft.eventDateStart,
      eventDateEnd: draft.eventDateEnd,
      activities: draft.activities,
      optionalActivities: draft.optionalActivities,
      weatherCondition: draft.weatherCondition,
    }
    createEvent.mutate(input, {
      onSuccess: (result) => {
        reset()
        navigate(`/me/events/${result.event.id}`)
      },
    })
  }

  const errorText = mutationErrorText(createEvent.error, t)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('eventNew.stepReview')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <StatGrid>
          <Stat label={t('eventNew.summaryTitle')} value={title.length > 0 ? title : t('common.unknown')} />
          <Stat label={t('eventNew.cityLabel')} value={t(cityLabelKey(draft.city))} />
          <Stat label={t('eventNew.summaryTime')} value={timeValue} />
          <Stat label={t('eventNew.summaryWeather')} value={t(`weather.${draft.weatherCondition}`)} />
          <Stat
            label={t('eventNew.summaryActivities')}
            value={
              draft.activities.length === 0
                ? t('common.unknown')
                : draft.activities
                    .map(
                      (activity) =>
                        `${t(activityLabelKey(activity))}${
                          draft.optionalActivities.includes(activity)
                            ? `（${t('events.requirementOptional')}）`
                            : ''
                        }`,
                    )
                    .join(' / ')
            }
          />
        </StatGrid>

        {blocker ? (
          <ErrorAlert message={blocker} />
        ) : null}

        {errorText ? (
          <ErrorAlert message={errorText} />
        ) : null}

        <WizardFooter
          backTo="/me/events/new/weather"
          backLabel={t('common.back')}
          nextLabel={createEvent.isPending ? t('eventNew.submitting') : t('eventNew.submit')}
          onNext={submit}
          nextDisabled={!canSubmit}
          busy={createEvent.isPending}
          hint={t('eventNew.reviewHint')}
        />
      </CardContent>
    </Card>
  )
}
